import {
  EDITED_CELL_CLASSNAME,
  getCell,
  type ICell as ITableCell,
  type ICellCoordinates,
  type ICellPath,
  type IRow as ITableRow,
  setCell
} from '@decathlon/react-table'
import EditableCell from 'components/common/cells/editable-cell'

import { formatValue, getClassName, type IDataCoordinates } from 'core/utils/cells'
import { getEditableCell } from 'core/utils/rows'
import type { IMask } from 'core/api/rest/queries'
import type { ICache, IComputedCell, IPathCache, IPathRow, IRows } from './index'

export const getCellPaths = (
  rows: IRows<IPathRow>,
  rowId: string, //targeted row id
  cellIndex: number, //column index
  cache: IPathCache, // parent-children mapping
  cellWithSubItemsIndex = 0, // ex: ACCOUNT cell index
  headersCount = 0 //number of headers
): ICellPath[] => {
  const { parentsMapping, rootRowIds } = cache
  const paths: ICellPath[] = []
  const parentsIds = parentsMapping[rowId]

  if (!parentsIds) {
    //it is root
    // use rows mapping
    const updatedRowIndex = rootRowIds.indexOf(rowId) + headersCount
    return [[{ rowIndex: updatedRowIndex, cellIndex }]]
  }
  //otherwise it is not root
  parentsIds.forEach(parentId => {
    const updatedRowIndex = rows[parentId].children.indexOf(rowId)
    // we use cellWithSubItemsIndex instead of updatedCellIndex because we have to access to the next level
    const updatedCellPath = getCellPaths(
      rows,
      parentId,
      cellWithSubItemsIndex,
      cache,
      cellWithSubItemsIndex,
      headersCount
    )
    const cellPathPart: ICellCoordinates = { rowIndex: updatedRowIndex, cellIndex }
    const newUpdatedQueries = updatedCellPath.map(updatedCellPathPart => [...updatedCellPathPart, cellPathPart])
    paths.push(...newUpdatedQueries)
  })
  return paths
}

export const updateCellValue = (
  currentCell: ITableCell,
  value: number,
  initial_value: number,
  mask: IMask
): ITableCell => {
  if (!currentCell) return null
  const formattedValue = formatValue(value, mask)
  const formattedInitialValue = formatValue(initial_value, mask)
  let isEdited = false
  const isNotNull = value !== null || initial_value !== null
  if (isNotNull) {
    isEdited = formattedValue !== formattedInitialValue
  }
  const { className } = currentCell
  // if it's an editable cell and cells with custom content
  if (currentCell.cellContent) {
    return {
      ...currentCell,
      cellContentProps: {
        ...currentCell.cellContentProps,
        value,
        isEdited
      }
    }
  }
  let newClassName = className || ''
  if (!isEdited) {
    newClassName = newClassName.replace(EDITED_CELL_CLASSNAME, '').trim()
  } else {
    newClassName = newClassName.includes(EDITED_CELL_CLASSNAME) ? className : EDITED_CELL_CLASSNAME
  }
  return { ...currentCell, value: formattedValue, className: newClassName }
}

/**
 * triggered after successfully save
 * replace initial value by updated value and remove edited state
 */
export const persistCellValue = (currentCell: ITableCell): ITableCell => {
  const { className } = currentCell
  // if it's an editable cell
  if (currentCell.cellContent) {
    return {
      ...currentCell,
      cellContentProps: {
        ...currentCell.cellContentProps,
        // @ts-ignore cellContentProps is generic
        initial_value: currentCell.cellContentProps.value,
        isEdited: false
      }
    }
  }
  const newClassName = (className && className.replace(EDITED_CELL_CLASSNAME, '')) || ''
  return { ...currentCell, className: newClassName.trim() }
}

export const updateTableRows = (
  computedCells: IComputedCell[],
  tableRows: ITableRow[],
  rows: IRows,
  cache: ICache,
  cellWithSubItemsIndex = 0,
  shouldPersist = false
): ITableRow[] => {
  const { columnsIndexMapping } = cache
  let updatedTableRows = tableRows
  computedCells.forEach(computedCell => {
    const row = rows[computedCell.label_code]
    if (row && (!row.is_hidden || row.is_child)) {
      Object.keys(computedCell.data).forEach(columnId => {
        const cellPaths = getCellPaths(
          rows,
          computedCell.label_code,
          columnsIndexMapping[columnId],
          cache,
          cellWithSubItemsIndex,
          1
        )
        const rowCell = row.cells[columnId]
        if (rowCell == null) return
        cellPaths.forEach(cellPath => {
          const currentCell = getCell(updatedTableRows, cellPath)
          const { value } = computedCell.data[columnId]
          const newCell = shouldPersist
            ? persistCellValue(currentCell)
            : updateCellValue(currentCell, value, rowCell.initial_value, rowCell.mask || row.mask)
          updatedTableRows = setCell(updatedTableRows, cellPath, newCell)
        })
      })
    }
  })
  return updatedTableRows
}

export const updateTableRowsWithUpdater = (
  cellsCoordinates: IDataCoordinates[],
  tableRows: ITableRow[],
  rows: IRows,
  cache: ICache,
  updater: (
    currentCell: ITableCell<IDataCoordinates>,
    dataCoordinates?: IDataCoordinates
  ) => ITableCell<IDataCoordinates>,
  cellWithSubItemsIndex = 0
): ITableRow[] => {
  const { columnsIndexMapping } = cache
  let updatedTableRows = tableRows
  cellsCoordinates.forEach(cellCoordinates => {
    const { rowId, columnId } = cellCoordinates
    const row = rows[rowId]
    if (row) {
      const cellPaths = getCellPaths(rows, rowId, columnsIndexMapping[columnId], cache, cellWithSubItemsIndex, 1)
      cellPaths.forEach(cellPath => {
        const cellToUpdate = getCell(updatedTableRows, cellPath)
        const updatedCell = updater(cellToUpdate, { rowId, columnId })
        updatedTableRows = setCell(updatedTableRows, cellPath, updatedCell)
      })
    }
  })
  return updatedTableRows
}

export const getPersistedTableRows = (
  computedCells: IComputedCell[],
  tableRows: ITableRow[],
  rows: IRows,
  cache: ICache,
  cellWithSubItemsIndex = 0
): ITableRow[] => {
  return updateTableRows(computedCells, tableRows, rows, cache, cellWithSubItemsIndex, true)
}

export const toEditableCell = <IGenericRows extends IRows = IRows>(
  tableRows: ITableRow[],
  rows: IGenericRows,
  rowId: string,
  columnId: string,
  cache: ICache,
  component = EditableCell,
  cellWithSubItemsIndex = 0
): ITableRow[] => {
  let newTableRows = tableRows
  const { columnsIndexMapping } = cache
  const cellPaths = getCellPaths(rows, rowId, columnsIndexMapping[columnId], cache, cellWithSubItemsIndex, 1)
  const row = rows[rowId]
  const cell = row.cells[columnId]
  cellPaths.forEach(cellPath => {
    const currentCell = getCell(newTableRows, cellPath)
    const newCell = getEditableCell(
      { value: cell.value, isEdited: false, initial_value: cell.value },
      row.mask,
      rowId,
      columnId,
      currentCell.id,
      component
    )
    newTableRows = setCell(newTableRows, cellPath, newCell)
  })
  return newTableRows
}

export const toDefaultCell = <IGenericRows extends IRows = IRows>(
  tableRows: ITableRow[],
  rows: IGenericRows,
  rowId: string,
  columnId: string,
  cache: ICache,
  cellWithSubItemsIndex = 0
): ITableRow[] => {
  let newTableRows = tableRows
  const { columnsIndexMapping } = cache
  const cellPaths = getCellPaths(rows, rowId, columnsIndexMapping[columnId], cache, cellWithSubItemsIndex, 1)
  const row = rows[rowId]
  const cell = row.cells[columnId]
  const mask = cell?.mask || row?.mask
  const getCustomClassName = mask?.is_percentage ? getClassName : undefined

  cellPaths.forEach(cellPath => {
    const currentCell = getCell(newTableRows, cellPath)
    const newCell = {
      id: currentCell.id,
      value: formatValue(cell.value, row.mask),
      getClassName: getCustomClassName
    }
    newTableRows = setCell(newTableRows, cellPath, newCell)
  })
  return newTableRows
}

export const sortTableColumns = (
  rows: ITableRow[],
  headerCells: ITableRow['cells'],
  columnsOrder: Record<string, number>
): ITableRow[] => {
  const sortedRows: ITableRow[] = []

  for (const row of rows) {
    const newRow: ITableRow = { ...row }
    const newCells: ITableRow['cells'] = []

    for (let i = 0; i < newRow.cells.length; i++) {
      const headerId = headerCells[i].id
      const cell = newRow.cells[i]
      const newCell = { ...cell }

      if (newCell.subItems) {
        newCell.subItems = sortTableColumns(newCell.subItems, headerCells, columnsOrder)
      }

      if (headerId === 'ACCOUNTS') {
        newCells[columnsOrder[`header_${headerId}`]] = newCell
      } else {
        newCells[columnsOrder[headerId]] = newCell
      }
    }

    newRow.cells = newCells
    sortedRows.push(newRow)
  }

  return sortedRows
}

export const getTableColumnsOrder = (headerCells: ITableRow['cells']): Record<string, number> => {
  return headerCells.reduce<Record<string, number>>((acc, headerCell, index) => {
    if (headerCell.id === 'ACCOUNTS') {
      acc[`header_${headerCell.id}`] = index
    } else {
      acc[headerCell.id] = index
    }
    return acc
  }, {})
}
